import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import {BehaviorSubject, catchError, firstValueFrom, map} from "rxjs";
import {Publication, PublicationEndpointResponse} from "../models/publication.model";
import {FilterService} from "./filter.service";
import {FeedbackService} from "./feedback.service";
import {Filter} from "../models/filter";
import {CTMSMilestone, CTMSMilestoneEndpointResponse, SyncAllResponse, SyncSource, SyncSingleResponse, SYNC_SOURCE_LABELS} from "../models/ctmsmilestone.model";
import {TimelineData} from "../timeline/timeline.component";
import {DataObject} from "../models/metadata";
import {CustomMilestone, CustomMilestoneEndpointResponse} from "../models/custom-milestone.model";
import {Activity, ActivityEndpointResponse} from "../models/activity.model";
import {VVPMActivity, VVPMActivityEndpointResponse} from "../models/vvpm-activity.model";
import {Congress, CongressEndpointResponse} from "../models/congress.model";
import { Observable } from 'rxjs';
import { StrategicPillarService } from './strategic-pillar.service';

export type LastSyncData = {
  lastPublicationSync: Date | undefined,
  lastMilestoneSync: Date | undefined,
  lastCongressSync: Date | undefined,
  lastVVPMSync: Date | undefined
}

@Injectable({
  providedIn: 'root'
})
export class TimelineDataService {
  private initialFetchDone = false;
  private readonly dataURLs = {
    publications: '/api/publications',
    refreshPublications: '/api/publications/refresh',
    ctmsMilestones: '/api/ctms',
    refreshMilestones: '/api/ctms/refresh',
    customMilestones: '/api/custom-milestones',
    activities: '/api/custom-activities',
    vvpmActivities: '/api/vvpm-activities',
    congresses: '/api/congresses',
    googleSlides: '/api/google-slides'
  }

  private readonly syncEndpoints: Record<SyncSource, string> = {
    publications: '/api/jobs/trigger/publications',
    ctms: '/api/jobs/trigger/ctms',
    congress: '/api/jobs/trigger/congress',
    veeva: '/api/jobs/trigger/veeva',
  }
  private readonly defaultResponses = {
    publications: {
      isOld: false,
      lastUpdate: "",
      publications: []
    } as PublicationEndpointResponse,
    ctmsMilestones: {
      isOld: false,
      lastUpdate: "",
      ctmsMilestones: []
    } as CTMSMilestoneEndpointResponse,
    customMilestones: {
      customMilestones: []
    } as CustomMilestoneEndpointResponse,
    activities: {
      customActivities: []
    } as ActivityEndpointResponse,
    vvpmActivities: {
      isOld: false,
      lastUpdate: "",
      vvpmActivities: []
    } as VVPMActivityEndpointResponse,
    congresses: {
      isOld: false,
      lastUpdate: "",
      congresses: []
    } as CongressEndpointResponse
  }

  private timelineDataSubject = new BehaviorSubject<TimelineData>({
    aboveTimeline: {
      ctmsMilestones: [],
      customMilestones: [],
      congresses: []
    },
    belowTimeline: {
      publications: [],
      activities: [],
      vvpmActivities: []
    }
  })
  timelineData = this.timelineDataSubject.asObservable()
  private lastSyncDataSubject = new BehaviorSubject<LastSyncData>({
    lastMilestoneSync: undefined,
    lastPublicationSync: undefined,
    lastCongressSync: undefined,
    lastVVPMSync: undefined
  })
  lastSyncData = this.lastSyncDataSubject.asObservable()
  filters!: Filter

  constructor(
    private http: HttpClient, 
    private filterService: FilterService, 
    public feedbackService: FeedbackService,
    private strategicPillarService: StrategicPillarService
  ) {
    this.filterService.filterData.subscribe(filters => {
      this.filters = filters
      // Only fetch if this is not the initial filter or if initial fetch was already done
      if (this.initialFetchDone || JSON.stringify(filters) !== JSON.stringify(new Filter())) {
        console.log('Fetching data due to filter change:', this.initialFetchDone ? 'Initial fetch done' : 'Non-default filter');
        this.fetchData(true)
      } else {
        console.log('Skipping duplicate fetch for initial default filter');
      }
    })
  }

  async fetchData(ignoreOld = false) {
    console.log('Timeline data fetch initiated', this.initialFetchDone ? 'subsequent fetch' : 'first fetch');
    this.initialFetchDone = true;
    
    const [pubResponse, ctmResponse, customMilestones, activities, vvpmActivities, congressResponse] = await Promise.all([
      firstValueFrom(this.fetchPublications()),
      firstValueFrom(this.fetchCTMSMilestones()),
      firstValueFrom(this.fetchCustomMilestones()),
      firstValueFrom(this.fetchActivities()),
      firstValueFrom(this.fetchVVPMActivities()),
      firstValueFrom(this.fetchCongresses())
    ]);
    
    console.log('Timeline data fetch completed');
    
    if(!ignoreOld) {
      const staleSources: string[] = [];
      if (pubResponse.isOld) staleSources.push('Publications');
      if (ctmResponse.isOld) staleSources.push('Milestones');
      if (congressResponse.isOld) staleSources.push('Congresses');
      if (vvpmActivities.isOld) staleSources.push('VVPM');
      if (staleSources.length > 0) {
        this.feedbackService.emitCriticalWarning(
          staleSources.join(', ') + (staleSources.length === 1 ? ' has' : ' have') + ' outdated data!'
        );
      }
    }

    this.timelineDataSubject.next({
      aboveTimeline: {
        ctmsMilestones: ctmResponse.ctmsMilestones,
        customMilestones: customMilestones.customMilestones,
        congresses: congressResponse.congresses,
      },
      belowTimeline: {
        publications: pubResponse.publications,
        activities: activities.customActivities,
        vvpmActivities: vvpmActivities.vvpmActivities,
      }
    })

    this.lastSyncDataSubject.next({
      lastPublicationSync: new Date(pubResponse.lastUpdate),
      lastMilestoneSync: new Date(ctmResponse.lastUpdate),
      lastCongressSync: new Date(congressResponse.lastUpdate),
      lastVVPMSync: new Date(vvpmActivities.lastUpdate),
    })
  }

  // Note: We no longer need the enhanceEntitiesWithStrategicPillars and applyStrategicPillarsToEntities methods
  // since the API now includes strategicPillars directly in the responses

  fetchPublications() {
    return this.http.get<PublicationEndpointResponse>(this.dataURLs.publications, {params: this.getFilterParams()})
      .pipe(
        catchError(this.feedbackService.handleError<PublicationEndpointResponse>('getPublications', this.defaultResponses.publications)),
        map(result => this.parsePublication(result))
      )
  }

  fetchCTMSMilestones() {
    return this.http.get<CTMSMilestoneEndpointResponse>(this.dataURLs.ctmsMilestones, {params: this.getFilterParams()})
      .pipe(
        catchError(this.feedbackService.handleError<CTMSMilestoneEndpointResponse>('getMilestones', this.defaultResponses.ctmsMilestones)),
        map(result => this.parseCTMSMilestones(result))
      )
  }
  
  fetchCustomMilestones() {
    return this.http.get<CustomMilestoneEndpointResponse>(this.dataURLs.customMilestones, {params: this.getFilterParams()})
      .pipe(
        catchError(this.feedbackService.handleError<CustomMilestoneEndpointResponse>('getCustomMilestones', this.defaultResponses.customMilestones)),
        map(result => this.parseCustomMilestones(result))
      )
  }

  fetchActivities(){
    return this.http.get<ActivityEndpointResponse>(this.dataURLs.activities, {params: this.getFilterParams()})
      .pipe(
        catchError(this.feedbackService.handleError<ActivityEndpointResponse>('getCustomActivities', this.defaultResponses.activities)),
        map(result => this.parseCustomActivities(result))
      )
  }

  fetchVVPMActivities(){
    // Fetch VVPM activities with filter parameters (timespan, studies, persons, targets, global search)
    return this.http.get<VVPMActivityEndpointResponse>(this.dataURLs.vvpmActivities, {params: this.getFilterParams()})
      .pipe(
        catchError(this.feedbackService.handleError<VVPMActivityEndpointResponse>('getVVPMActivities', this.defaultResponses.vvpmActivities)),
        map(result => this.parseVVPMActivities(result))
      )
  }
  
  fetchCongresses() {
    return this.http.get<CongressEndpointResponse>(this.dataURLs.congresses, {params: this.getFilterParams()})
      .pipe(
        catchError(this.feedbackService.handleError<CongressEndpointResponse>('getCongresses', this.defaultResponses.congresses)),
        map(result => this.parseCongresses(result))
      )
  }

  createGoogleSlide(filters: Filter): Observable<any> {
    const url = `${this.dataURLs.googleSlides}`;
    const params = this.getFilterParams(); // Optional: Use existing filter parameters
    return this.http.post(url, filters); // Send filters directly
  }

  async refreshData(){
    console.log("Manual refresh: milestones and publications");
    
    try {
      const response = await firstValueFrom(
        this.http.post<SyncAllResponse>('/api/jobs/trigger/sync-all', {})
      );
      
      await this.fetchData();
      this.feedbackService.emitSuccess('Sync complete: All data sources updated.');
    } catch (err: any) {
      const errorBody = err.error as SyncAllResponse | undefined;
      if (errorBody?.results) {
        const pubSuccess = errorBody.results.publications.success;
        const ctmsSuccess = errorBody.results.ctms.success;
        
        if (!pubSuccess && !ctmsSuccess) {
          this.feedbackService.emitError(
            `Sync failed: Publications (${errorBody.results.publications.error || 'unknown error'}), ` +
            `Milestones (${errorBody.results.ctms.error || 'unknown error'})`
          );
        } else {
          // Partial failure - show what succeeded and what failed
          const parts: string[] = [];
          if (pubSuccess) {
            parts.push('Publications updated');
          } else {
            parts.push(`Publications failed: ${errorBody.results.publications.error || 'unknown error'}`);
          }
          if (ctmsSuccess) {
            parts.push('Milestones updated');
          } else {
            parts.push(`Milestones failed: ${errorBody.results.ctms.error || 'unknown error'}`);
          }
          this.feedbackService.emitWarning(`Partial sync: ${parts.join('. ')}.`);
          await this.fetchData();
        }
      } else {
        this.feedbackService.emitError(`Sync failed: ${err.message || 'Unknown error'}`);
      }
    }
  }

  async refreshSingle(source: SyncSource): Promise<void> {
    const label = SYNC_SOURCE_LABELS[source];
    console.log(`Manual refresh: ${label}`);

    try {
      await firstValueFrom(
        this.http.post<SyncSingleResponse>(this.syncEndpoints[source], {})
      );

      await this.fetchData();
      this.feedbackService.emitSuccess(`${label} sync complete.`);
    } catch (err: any) {
      const errorBody = err.error as SyncSingleResponse | undefined;
      const detail = errorBody?.error || errorBody?.message || err.message || 'Unknown error';
      this.feedbackService.emitError(`${label} sync failed: ${detail}`);
    }
  }

  updateItem(data: DataObject){
    let items: Array<DataObject> = this.getTimelineItemsOfSameClass(data)!

    const idx = items.findIndex(d => d.abstractID == data.abstractID)

    if(idx >= 0){
      items = [...items.slice(0, idx), data, ...items.slice(idx+1)]
    }
    else{
      items.push(data)
    }

    this.updateTimelineItemsOfSameClass(data, items)
  }

  deleteItem(data: DataObject){
    let items: Array<DataObject> = this.getTimelineItemsOfSameClass(data)!

    items = items.filter(i => i.abstractID != data.abstractID)

    this.updateTimelineItemsOfSameClass(data, items)
  }

  getCurrentTimelineData(){
    return this.timelineDataSubject.value
  }

  /** Returns true if the BehaviorSubject holds any non-empty timeline data. */
  hasData(): boolean {
    const d = this.timelineDataSubject.value;
    return (
      d.aboveTimeline.ctmsMilestones.length > 0 ||
      d.aboveTimeline.customMilestones.length > 0 ||
      d.aboveTimeline.congresses.length > 0 ||
      d.belowTimeline.publications.length > 0 ||
      d.belowTimeline.activities.length > 0 ||
      d.belowTimeline.vvpmActivities.length > 0
    );
  }

  setNewTimelineData(data: TimelineData){
    this.timelineDataSubject.next(data)
  }

  private getTimelineItemsOfSameClass(o: DataObject){
    if(o instanceof Publication){
      return this.timelineDataSubject.value.belowTimeline.publications
    }
    else if(o instanceof CTMSMilestone){
      return this.timelineDataSubject.value.aboveTimeline.ctmsMilestones
    }
    else if(o instanceof CustomMilestone){
      return this.timelineDataSubject.value.aboveTimeline.customMilestones as Array<DataObject>
    }
    else if(o instanceof Activity){
      return this.timelineDataSubject.value.belowTimeline.activities as Array<DataObject>
    }
    else if(o instanceof VVPMActivity){
      return this.timelineDataSubject.value.belowTimeline.vvpmActivities as Array<DataObject>
    }

    return undefined
  }

  private updateTimelineItemsOfSameClass(o: DataObject, items: Array<DataObject>){
    const current = this.timelineDataSubject.value;
    const above = { ...current.aboveTimeline };
    const below = { ...current.belowTimeline };

    if(o instanceof Publication){
      below.publications = items as Array<Publication>;
    }
    else if(o instanceof CTMSMilestone){
      above.ctmsMilestones = items as Array<CTMSMilestone>;
    }
    else if(o instanceof CustomMilestone){
      above.customMilestones = items as Array<CustomMilestone>;
    }
    else if(o instanceof Activity){
      below.activities = items as Array<Activity>;
    }
    else if(o instanceof VVPMActivity){
      below.vvpmActivities = items as Array<VVPMActivity>;
    }

    this.timelineDataSubject.next({ aboveTimeline: above, belowTimeline: below });
  }

  private getFilterParams() {
    const params: any = {
      'timespanStart': "" + this.filters.timespan.start.getTime(),
      'timespanEnd': "" + this.filters.timespan.end.getTime(),
      'studies[]': this.filters.studies?.map(s => JSON.stringify(s)) || [],
      'persons[]': this.filters.persons || [],
      'targets[]': this.filters.targets || [],
      'global': this.filters.global || ""
    };
    
    // Add strategic pillars and audiences if they exist
    if (this.filters.strategicPillars && this.filters.strategicPillars.length > 0) {
      params['strategicPillars[]'] = this.filters.strategicPillars.map(sp => sp.id);
    }
    
    if (this.filters.audiences && this.filters.audiences.length > 0) {
      params['audiences[]'] = this.filters.audiences.map(a => a.title);
    }
    
    return new HttpParams({ fromObject: params });
  }

  private parsePublication(response: PublicationEndpointResponse): PublicationEndpointResponse {
    const publications = response.publications?.map(p => {
      return Object.assign<Publication, Object>(new Publication(), p)
    })
    return {
      ...response,
      publications
    }
  }

  private parseCTMSMilestones(response: CTMSMilestoneEndpointResponse): CTMSMilestoneEndpointResponse {
    const ctmsMilestones = response.ctmsMilestones?.map(cm => Object.assign(new CTMSMilestone(), cm))
    return {
      ...response,
      ctmsMilestones
    }
  }

  private parseCustomMilestones(response: CustomMilestoneEndpointResponse): CustomMilestoneEndpointResponse{
    const customMilestones = response.customMilestones.map(ccm => {
      const ccmInstance = Object.assign(new CustomMilestone(), ccm)
      ccmInstance.date = new Date(ccmInstance.date)

      return ccmInstance
    })

    return {customMilestones}
  }

  private parseCustomActivities(response: ActivityEndpointResponse): ActivityEndpointResponse{
    const customActivities = response.customActivities.map(ca => {
      const caInstance = Object.assign(new Activity(), ca)
      caInstance.dueDate = new Date(caInstance.dueDate)

      return caInstance
    })

    return {customActivities}
  }

  private parseVVPMActivities(response: VVPMActivityEndpointResponse | Array<VVPMActivity>): VVPMActivityEndpointResponse{
    // Handle both direct array response and wrapped object response
    let activitiesArray: Array<VVPMActivity> = [];
    let isOld = false;
    let lastUpdate = '';
    
    if (Array.isArray(response)) {
      // API returns array directly (legacy format)
      activitiesArray = response;
    } else if (response && response.vvpmActivities) {
      // API returns wrapped object with metadata
      activitiesArray = response.vvpmActivities;
      isOld = response.isOld || false;
      lastUpdate = response.lastUpdate || '';
    }
    
    const vvpmActivities = activitiesArray.map(va => {
      return Object.assign(new VVPMActivity(), va)
    })

    return {
      isOld,
      lastUpdate,
      vvpmActivities
    }
  }

  private parseCongresses(response: CongressEndpointResponse): CongressEndpointResponse {
    const congresses = (response.congresses || []).map(c => Object.assign(new Congress(), c));
    return {
      ...response,
      congresses
    }
  }

}
